import { keyArray } from "@solid-primitives/keyed"
import { createEffect, onCleanup, type Accessor } from "solid-js"
import { Group, type Scene } from "three"
import type { Nullable } from "vite-node"
import { useNodeNeighborBaseFloorElevationReactiveMap } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import { useEntityByEntityTypeIdReactiveMap } from "../../../../../rx/reactive_map/useEntityByEntityTypeIdReactiveMap"
import type { Engine } from "../../../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../../../core/engine/EntityGraphNode/EntityGraphNode.type"
import type { ForestEntity } from "../../../../core/entity/EntityType/Forest"
import { getForestEntsFromMap } from "../../../../core/entity/entityTypeMetaList.generated"
import nullIfEmptyArray from "../../../../ldsh/nullIfEmptyArray"
import disposeRecursive from "../../../fn/disposeRecursive"
import createForestTerrainClusterGroupV1Quaternius from "./createForestTerrainClusterGroupV1Quaternius"
import createForestTerrainClusterGroupV2Simple from "./createForestTerrainClusterGroupV2Simple"
import createInitialForestClusters from "./createInitialForestClusters"

// const FADE_DURATION = 1000

const enum ForestVersion {
  V1 = 1,
  V2 = 2,
}

const FOREST_VERSION : ForestVersion = ForestVersion.V2 as ForestVersion

const makeForestTerrainClusterGroup = FOREST_VERSION === ForestVersion.V1 ? createForestTerrainClusterGroupV1Quaternius : createForestTerrainClusterGroupV2Simple

// pointing diagonally in the same direction
export default function createForestTerrainEffect(
  engine: Engine,
  scene: Scene,
): void {
  const group = new Group()
  group.name = 'ForestsGroup'
  scene.add(group)

  const neighborBaseElevationReactiveMap = useNodeNeighborBaseFloorElevationReactiveMap(engine)
  
  const entityByTypeMap = useEntityByEntityTypeIdReactiveMap(engine)

  function getChunks() {
    return createInitialForestClusters(getForestEntsFromMap(entityByTypeMap))
  }

  // we'll use the first ID as the chunk ID
  function getChunkId(ents: Array<EntityGraphNode<ForestEntity>>) {
    return ents[0].id
  }

  const mapped = keyArray(getChunks, getChunkId, (
    getList: Accessor<Array<EntityGraphNode<ForestEntity>>>,
    // getIndex
  ) => {
    // console.log('mapped', {index: getIndex(), chunk: getChunkId(getList())}, getList().length)

    let clusterGroup: Nullable<Awaited<ReturnType<typeof makeForestTerrainClusterGroup>>>

    // onMount(() => {
    //   console.log('mounted', {chunk: getChunkId(getList()), len: getList().length})
    // })

    onCleanup(async () => {
      // console.log('cleanup', {chunk: getChunkId(getList()), len: getList().length})
      // fadeOutMaterialAndCleanupObject3D(clusterGroup, FADE_DURATION)
      disposeRecursive(clusterGroup)
    })

    createEffect((): void => {
      const nextList = getList()

      createEffect(() => {
        const prevClusterMesh = clusterGroup

        const nextMeshPromise = (nullIfEmptyArray(nextList) && makeForestTerrainClusterGroup(nextList, neighborBaseElevationReactiveMap))
        if (nextMeshPromise) {
          nextMeshPromise.then((nextMesh) => {
            // console.log('nextMesh', nextMesh)
            if (nextMesh) {
              // nextMesh.position.y = DEFAULT_LAND_TILE_ELEVATION - 0.25
              group.add(nextMesh)
            }
            if (prevClusterMesh) {
              prevClusterMesh.visible = false
              prevClusterMesh.removeFromParent()
              disposeRecursive(prevClusterMesh)
            }
            // fadeOutMaterialAndCleanupObject3D(prevMesh, FADE_DURATION)
            clusterGroup = nextMesh
          })
        }
      })
    })

    return null
  })
  // we need this effect otherwise nothing calls the mapped function
  createEffect(mapped)
}
